import React, { useState, useEffect } from 'react';
import { 
  Clock, User, Target, TrendingUp, CheckCircle, XCircle, 
  FileText, Zap, Package, Filter, RefreshCw
} from 'lucide-react';
import { getAuditLogs } from '../services/api';

const actionConfig = {
  login: { icon: User, color: 'bg-slate-100 text-slate-600', label: 'Login' },
  create_decision: { icon: Target, color: 'bg-blue-100 text-blue-600', label: 'Decision Created' },
  update_decision: { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-600', label: 'Decision Updated' },
  pipeline_move: { icon: TrendingUp, color: 'bg-violet-100 text-violet-600', label: 'Pipeline Move' },
  create_execution: { icon: Package, color: 'bg-amber-100 text-amber-600', label: 'Execution Created' },
  generate_committee_pack: { icon: FileText, color: 'bg-teal-100 text-teal-600', label: 'Committee Pack Generated' },
  generate_committee_pack_pdf: { icon: FileText, color: 'bg-teal-100 text-teal-600', label: 'Committee Pack PDF' },
  dismiss_intervention: { icon: XCircle, color: 'bg-red-100 text-red-600', label: 'Intervention Dismissed' },
  act_on_intervention: { icon: Zap, color: 'bg-orange-100 text-orange-600', label: 'Intervention Acted' },
  register: { icon: User, color: 'bg-green-100 text-green-600', label: 'User Registered' },
  generate_brief: { icon: FileText, color: 'bg-indigo-100 text-indigo-600', label: 'AI Brief Generated' },
  generate_molecule_summary: { icon: FileText, color: 'bg-cyan-100 text-cyan-600', label: 'Molecule Summary Generated' },
  generate_strategic_advice: { icon: Zap, color: 'bg-purple-100 text-purple-600', label: 'Strategic Advice Generated' },
  update_weights: { icon: Target, color: 'bg-slate-100 text-slate-600', label: 'Scoring Weights Updated' },
  update_review: { icon: CheckCircle, color: 'bg-amber-100 text-amber-600', label: 'Review Status Updated' },
};

const filterActions = [
  { value: '', label: 'All Activity' },
  { value: 'create_decision', label: 'Decisions' },
  { value: 'pipeline_move', label: 'Pipeline Moves' },
  { value: 'create_execution', label: 'Executions' },
  { value: 'generate_committee_pack', label: 'Committee Packs' },
  { value: 'generate_brief', label: 'AI Briefs' },
  { value: 'login', label: 'Logins' },
];

function TimelineEvent({ log }) {
  const config = actionConfig[log.action] || { icon: Clock, color: 'bg-slate-100 text-slate-600', label: log.action };
  const Icon = config.icon;
  const details = log.details || {};

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const renderDetails = () => {
    const parts = [];
    if (details.drug_id) parts.push(`Drug: ${details.drug_name || details.drug_id}`);
    if (details.generic_id) parts.push(`Generic: ${details.generic_name || details.generic_id}`);
    if (details.action) parts.push(`Action: ${details.action.replace('_', ' ')}`);
    if (details.decision_id) parts.push(`Decision: ${details.decision_id.slice(0, 8)}...`);
    if (details.new_stage) parts.push(`Stage: ${details.from_stage || '?'} → ${details.new_stage}`);
    if (details.pack_id) parts.push(`Pack: ${details.pack_id.slice(0, 8)}...`);
    if (details.status) parts.push(`Status: ${details.status}`);
    if (details.email) parts.push(details.email);
    return parts;
  };

  const detailParts = renderDetails();

  return (
    <div className="flex gap-4 group" data-testid={`timeline-event-${log.action}`}>
      <div className="flex flex-col items-center">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color} ring-4 ring-white`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="w-px flex-1 bg-slate-200 group-last:bg-transparent" />
      </div>
      <div className="pb-6 flex-1">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-slate-900">{config.label}</h3>
          <span className="text-xs text-slate-400">{formatTimestamp(log.timestamp)}</span>
        </div>
        {log.user_name && (
          <p className="text-sm text-slate-500 mt-0.5">
            by {log.user_name}
            {log.user_role && <span className="text-xs text-slate-400 ml-1">({log.user_role})</span>}
          </p>
        )}
        {detailParts.length > 0 && (
          <div className="mt-2 p-3 bg-slate-50 rounded-lg">
            {detailParts.map((part, i) => (
              <p key={i} className="text-sm text-slate-600">{part}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TimelinePage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await getAuditLogs(200);
      setLogs(res.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = filter 
    ? logs.filter(l => l.action === filter || l.action?.startsWith(filter))
    : logs;

  // Exclude login events from the default "All" view to reduce noise
  const displayLogs = !filter 
    ? filteredLogs.filter(l => l.action !== 'login')
    : filteredLogs;

  // Group by date
  const groupedLogs = {};
  displayLogs.forEach(log => {
    const date = log.timestamp ? new Date(log.timestamp).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    }) : 'Unknown Date';
    if (!groupedLogs[date]) groupedLogs[date] = [];
    groupedLogs[date].push(log);
  });

  return (
    <div className="p-6" data-testid="timeline-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-1">Decision Timeline</h1>
          <p className="text-slate-500">System of record for all governance activity</p>
        </div>
        <button onClick={fetchLogs} className="btn-secondary flex items-center gap-2" data-testid="refresh-timeline-btn">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="timeline-filters">
        <Filter className="w-4 h-4 text-slate-400" />
        {filterActions.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              filter === f.value 
                ? 'bg-[#0A2540] text-white' 
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`filter-${f.value || 'all'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex gap-4">
              <div className="skeleton w-10 h-10 rounded-full flex-shrink-0" />
              <div className="flex-1">
                <div className="skeleton h-5 w-48 mb-2" />
                <div className="skeleton h-4 w-32" />
              </div>
            </div>
          ))}
        </div>
      ) : displayLogs.length > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedLogs).map(([date, logs]) => (
            <div key={date}>
              <div className="sticky top-0 bg-[#F9FAFB] z-10 pb-2 pt-1">
                <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">{date}</h2>
              </div>
              <div className="ml-1">
                {logs.map((log, i) => (
                  <TimelineEvent key={`${log.timestamp}-${i}`} log={log} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card-premium p-12 text-center">
          <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">No activity recorded yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Actions like decisions, pipeline moves, and executions will appear here
          </p>
        </div>
      )}
    </div>
  );
}
